import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ReactFlow, ReactFlowProvider, Background, BackgroundVariant, Controls, MiniMap, Panel,
  useNodesState, useEdgesState, useReactFlow, addEdge, reconnectEdge, ConnectionMode, MarkerType,
  getNodesBounds, getViewportForBounds,
  type Edge, type Connection, type OnNodeDrag, type Viewport, type NodeChange, type EdgeChange,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { toPng } from 'html-to-image'
import {
  Plus, Save, Undo2, Redo2, Download, Upload, Maximize2, Minimize2, Copy, Trash2, Type, Square,
  AlignStartVertical, AlignCenterVertical, AlignEndVertical, AlignStartHorizontal, AlignCenterHorizontal,
  AlignEndHorizontal, AlignHorizontalDistributeCenter, AlignVerticalDistributeCenter, BringToFront, SendToBack,
  ImageIcon, FileJson, Pencil, Eye, Magnet, Search, X, ExternalLink, Network, RefreshCw, Link as LinkIcon, MapPin,
} from 'lucide-react'
import { api } from '../../utils/api'
import {
  nodeTypes, DEVICE_KINDS, KIND_MAP, guessKind, EditModeContext, DiagramActionsContext, deviceBoxWidth,
  type DiagramNode, type DeviceData, type ZoneData, type TextData,
} from './diagramNodes'
import { edgeTypes, type PortEdgeData } from './diagramEdges'

// ─── Types ───────────────────────────────────────────────────────────────────
interface SiteRow { id: string; name: string; company?: { id: string; name: string } | null }
interface DiagramRow { id: string; name: string; description: string | null; siteId: string; updatedBy: string | null; updatedAt: string }
interface DiagramFull extends DiagramRow { data: DiagramDoc }
interface SiteAsset { id: string; assetCode: string | null; name: string; type: string; category: string | null; brand: string | null; model: string | null; ipAddress: string | null; macEthernet: string | null; status: string; serialNumber: string | null; images: string[] }

type EdgeData = PortEdgeData
type DiagramEdge = Edge<EdgeData>

interface Settings {
  bg: string; grid: 'dots' | 'lines' | 'cross' | 'none'; gridGap: number; snap: boolean
  minimap: boolean; stats: boolean; edgeType: string; edgePreset: string
}
interface DiagramDoc { nodes?: DiagramNode[]; edges?: DiagramEdge[]; viewport?: Viewport; settings?: Partial<Settings> }

const DEFAULT_SETTINGS: Settings = {
  bg: '#f8fafc', grid: 'dots', gridGap: 20, snap: true, minimap: true, stats: true, edgeType: 'straight', edgePreset: 'ethernet',
}

const EDGE_PRESETS: { key: string; label: string; data: EdgeData }[] = [
  { key: 'ethernet', label: 'Ethernet / LAN',     data: { color: '#1d4ed8', width: 2,   dash: 'solid' } },
  { key: 'fiber',    label: 'Fiber',              data: { color: '#d946ef', width: 3,   dash: 'solid' } },
  { key: 'wan',      label: 'WAN / Leased line',  data: { color: '#7f1d1d', width: 4.5, dash: 'solid' } },
  { key: 'trunk',    label: 'Trunk / Uplink',     data: { color: '#0f766e', width: 3,   dash: 'solid' } },
  { key: 'vpn',      label: 'VPN / Tunnel',       data: { color: '#f59e0b', width: 2,   dash: 'dashed', animated: true } },
  { key: 'wireless', label: 'Wireless',           data: { color: '#16a34a', width: 2,   dash: 'dotted' } },
  { key: 'backup',   label: 'Backup / Standby',   data: { color: '#9ca3af', width: 2,   dash: 'long' } },
]
const EDGE_TYPES = [
  { key: 'straight', label: 'เส้นตรง' }, { key: 'default', label: 'เส้นโค้ง' },
  { key: 'smoothstep', label: 'หักมุมมน' }, { key: 'step', label: 'หักมุม' },
]
const DASH: Record<string, string | undefined> = { solid: undefined, dashed: '7 5', dotted: '2 4', long: '14 6' }
const SWATCHES = ['#111827', '#6b7280', '#ffffff', '#dc2626', '#ea580c', '#f59e0b', '#fde68a', '#16a34a', '#99f6e4', '#0891b2', '#2563eb', '#bfdbfe', '#7c3aed', '#d946ef', '#7f1d1d', '#e5e7eb']

const uid = (p: string) => `${p}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`

// Derive React Flow visual props (style / markers / label styles) from edge.data
function decorateEdge(e: DiagramEdge): DiagramEdge {
  const d = e.data || {}
  const color = d.color || '#1d4ed8'
  const marker = { type: MarkerType.ArrowClosed, color, width: 16, height: 16 }
  return {
    ...e,
    animated: !!d.animated,
    style: { stroke: color, strokeWidth: d.width ?? 2, strokeDasharray: DASH[d.dash || 'solid'] },
    markerEnd: d.arrowEnd ? marker : undefined,
    markerStart: d.arrowStart ? marker : undefined,
    labelStyle: { fill: d.labelColor || '#111827', fontSize: d.labelSize || 11, fontWeight: 600 },
    labelShowBg: true,
    labelBgStyle: { fill: d.labelBg || '#ffffff', fillOpacity: 0.9 },
    labelBgPadding: [4, 2],
    labelBgBorderRadius: 3,
    interactionWidth: 14,
  }
}

const nodeW = (n: DiagramNode) => n.width ?? n.measured?.width ?? 0
const nodeH = (n: DiagramNode) => n.height ?? n.measured?.height ?? 0

function serializeNodes(ns: DiagramNode[]): DiagramNode[] {
  return ns.map(({ selected, dragging, measured, resizing, ...rest }: any) => rest)
}
function serializeEdges(es: DiagramEdge[]): DiagramEdge[] {
  return es.map(({ selected, ...rest }: any) => rest)
}

// Second line under a device linked to an asset: brand + model (falls back to the asset name)
function assetModel(a: SiteAsset): string | undefined {
  const b = a.brand?.trim(), m = a.model?.trim()
  if (m) return b && !m.toLowerCase().startsWith(b.toLowerCase()) ? `${b} ${m}` : m
  if (b) return b
  return a.name && a.name !== a.assetCode ? a.name : undefined
}
function assetNodeData(a: SiteAsset): Partial<DeviceData> {
  return { label: a.assetCode || a.name, sublabel: assetModel(a), assetId: a.id, assetCode: a.assetCode, showAssetCode: false }
}

function readLS(k: string): string | null { try { return localStorage.getItem(k) } catch { return null } }
function writeLS(k: string, v: string) { try { localStorage.setItem(k, v) } catch { /* ignore */ } }

// ─── Small UI helpers ────────────────────────────────────────────────────────
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  )
}
const inputCls = 'w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400'

function ColorField({ label, value, onChange, allowNone }: { label: string; value: string | undefined; onChange: (v: string) => void; allowNone?: boolean }) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <input type="color" value={value && value !== 'transparent' ? value : '#ffffff'} onChange={e => onChange(e.target.value)}
          className="w-8 h-7 p-0 border border-gray-200 rounded cursor-pointer bg-transparent"/>
        <input value={value || ''} onChange={e => onChange(e.target.value)} className={inputCls + ' font-mono'} placeholder={allowNone ? 'transparent' : '#000000'}/>
      </div>
      <div className="flex flex-wrap gap-1 mt-1.5">
        {allowNone && (
          <button type="button" title="ไม่มีสี" onClick={() => onChange('transparent')}
            className="w-4 h-4 rounded border border-gray-300 bg-white relative overflow-hidden">
            <span className="absolute inset-0 rotate-45 border-t border-red-500 top-1/2"/>
          </button>
        )}
        {SWATCHES.map(c => (
          <button type="button" key={c} title={c} onClick={() => onChange(c)}
            className={`w-4 h-4 rounded border ${value === c ? 'ring-2 ring-blue-500 ring-offset-1' : 'border-gray-300'}`} style={{ background: c }}/>
        ))}
      </div>
    </Field>
  )
}

function Seg<T extends string>({ value, options, onChange }: { value: T; options: { key: T; label: React.ReactNode; title?: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="flex rounded-lg border border-gray-200 overflow-hidden">
      {options.map(o => (
        <button type="button" key={o.key} title={o.title} onClick={() => onChange(o.key)}
          className={`flex-1 px-2 py-1 text-[11px] ${value === o.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
          {o.label}
        </button>
      ))}
    </div>
  )
}

function TbBtn({ title, onClick, disabled, active, children }: { title: string; onClick: () => void; disabled?: boolean; active?: boolean; children: React.ReactNode }) {
  return (
    <button type="button" title={title} onClick={onClick} disabled={disabled}
      className={`p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-35 disabled:hover:bg-transparent ${active ? 'bg-blue-50 text-blue-600' : ''}`}>
      {children}
    </button>
  )
}

// ─── Diagram name modal ──────────────────────────────────────────────────────
function DiagramModal({ initial, isNew, onClose, onSubmit, busy }: {
  initial: { name: string; description: string }; isNew: boolean; busy: boolean
  onClose: () => void; onSubmit: (v: { name: string; description: string; autoImport: boolean }) => void
}) {
  const [name, setName] = useState(initial.name)
  const [description, setDescription] = useState(initial.description)
  const [autoImport, setAutoImport] = useState(false)
  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <form className="bg-white rounded-2xl shadow-xl w-full max-w-md" onSubmit={e => { e.preventDefault(); if (name.trim()) onSubmit({ name, description, autoImport }) }}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">{isNew ? 'สร้าง Network Diagram' : 'แก้ไขชื่อ Diagram'}</h2>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="ชื่อ Diagram *">
            <input autoFocus value={name} onChange={e => setName(e.target.value)} className={inputCls + ' !text-sm'} placeholder="เช่น Layer 3 - IP / MPLS"/>
          </Field>
          <Field label="คำอธิบาย">
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className={inputCls + ' !text-sm'}/>
          </Field>
          {isNew && (
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={autoImport} onChange={e => setAutoImport(e.target.checked)} className="mt-0.5"/>
              <span>เพิ่มอุปกรณ์จาก Assets ของ Site นี้ให้อัตโนมัติ<br/><span className="text-xs text-gray-400">จัดเรียงตามประเภทอุปกรณ์ แล้วค่อยลากเส้นเชื่อมต่อเอง</span></span>
            </label>
          )}
        </div>
        <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">ยกเลิก</button>
          <button type="submit" disabled={busy || !name.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {busy ? 'กำลังบันทึก...' : isNew ? 'สร้าง' : 'บันทึก'}
          </button>
        </div>
      </form>
    </div>
  )
}

// Grid-layout asset nodes by device kind (network gear first), starting at (x0, y0)
function assetsToNodes(assets: SiteAsset[], x0: number, y0: number): DiagramNode[] {
  const order = DEVICE_KINDS.map(k => k.kind)
  const groups = new Map<string, SiteAsset[]>()
  for (const a of assets) {
    const k = guessKind(a)
    if (!groups.has(k)) groups.set(k, [])
    groups.get(k)!.push(a)
  }
  const out: DiagramNode[] = []
  let row = 0
  for (const k of order) {
    const list = groups.get(k)
    if (!list) continue
    list.forEach((a, i) => {
      out.push({
        id: uid('n'), type: 'device',
        position: { x: x0 + (i % 10) * 150, y: y0 + (row + Math.floor(i / 10)) * 150 },
        data: { kind: k, ...assetNodeData(a), ip: a.ipAddress || undefined },
      })
    })
    row += Math.ceil(list.length / 10)
  }
  return out
}

// ─── Main ────────────────────────────────────────────────────────────────────
export default function NetworkDiagramTab({ canEdit }: { canEdit: boolean }) {
  return <ReactFlowProvider><DiagramWorkspace canEdit={canEdit}/></ReactFlowProvider>
}

function DiagramWorkspace({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient()
  const rf = useReactFlow<DiagramNode, DiagramEdge>()
  const wrapperRef = useRef<HTMLDivElement>(null)

  // ── Site / diagram selection ──
  const { data: sites = [], isLoading: sitesLoading } = useQuery<SiteRow[]>({
    queryKey: ['masterdata', 'sites'], queryFn: () => api.get('/masterdata/sites').then(r => r.data).catch(() => []),
  })
  const [siteId, setSiteId] = useState<string>(() => readLS('nd.site') || '')
  useEffect(() => {
    if (sites.length && !sites.some(s => s.id === siteId)) setSiteId(sites[0].id)
  }, [sites, siteId])
  const site = sites.find(s => s.id === siteId)

  const { data: diagrams = [], isFetched: diagramsFetched } = useQuery<DiagramRow[]>({
    queryKey: ['network-diagrams', siteId], enabled: !!siteId,
    queryFn: () => api.get('/network-diagrams', { params: { siteId } }).then(r => r.data),
  })
  const [diagramId, setDiagramId] = useState<string>('')
  useEffect(() => {
    if (!siteId || !diagramsFetched) return
    if (diagrams.some(d => d.id === diagramId)) return
    const remembered = readLS(`nd.diagram.${siteId}`)
    setDiagramId(diagrams.find(d => d.id === remembered)?.id || diagrams[0]?.id || '')
  }, [siteId, diagrams, diagramsFetched, diagramId])
  useEffect(() => { if (siteId) writeLS('nd.site', siteId) }, [siteId])
  useEffect(() => { if (siteId && diagramId) writeLS(`nd.diagram.${siteId}`, diagramId) }, [siteId, diagramId])

  const { data: detail, isFetching: detailLoading } = useQuery<DiagramFull>({
    queryKey: ['network-diagram', diagramId], enabled: !!diagramId,
    queryFn: () => api.get(`/network-diagrams/${diagramId}`).then(r => r.data),
    staleTime: Infinity, refetchOnWindowFocus: false,
  })
  const { data: siteAssets = [] } = useQuery<SiteAsset[]>({
    queryKey: ['network-diagram-assets', siteId], enabled: !!siteId,
    queryFn: () => api.get('/network-diagrams/site-assets', { params: { siteId } }).then(r => r.data),
  })
  const assetMap = useMemo(() => new Map(siteAssets.map(a => [a.id, a])), [siteAssets])

  // ── Canvas state ──
  const [nodes, setNodes, onNodesChangeRaw] = useNodesState<DiagramNode>([])
  const [edges, setEdges, onEdgesChangeRaw] = useEdgesState<DiagramEdge>([])
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS)
  const [editing, setEditing] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [loadedId, setLoadedId] = useState<string>('')
  const nodesRef = useRef(nodes); nodesRef.current = nodes
  const edgesRef = useRef(edges); edgesRef.current = edges
  const isEditing = canEdit && editing

  // ── Undo / redo (JSON snapshots of nodes+edges) ──
  const past = useRef<string[]>([])
  const future = useRef<string[]>([])
  const [, bumpHistory] = useState(0)
  const snap = () => JSON.stringify({ n: serializeNodes(nodesRef.current), e: serializeEdges(edgesRef.current) })
  const takeSnapshot = useCallback(() => {
    const s = snap()
    if (past.current[past.current.length - 1] === s) return
    past.current.push(s)
    if (past.current.length > 100) past.current.shift()
    future.current = []
    bumpHistory(x => x + 1)
  }, [])
  const restore = (s: string) => {
    const { n, e } = JSON.parse(s)
    setNodes(n); setEdges((e as DiagramEdge[]).map(decorateEdge)); setDirty(true)
  }
  const undo = useCallback(() => {
    const cur = snap()
    while (past.current.length) {
      const s = past.current.pop()!
      if (s !== cur) { future.current.push(cur); restore(s); break }
    }
    bumpHistory(x => x + 1)
  }, [])
  const redo = useCallback(() => {
    const s = future.current.pop()
    if (!s) return
    past.current.push(snap()); restore(s)
    bumpHistory(x => x + 1)
  }, [])

  const diagramActions = useMemo(() => ({ beginChange: () => { takeSnapshot(); setDirty(true) } }), [takeSnapshot])

  // ── Load the selected diagram (only when the id changes — not on refetch after save) ──
  useEffect(() => {
    if (!diagramId) { setNodes([]); setEdges([]); setLoadedId(''); setDirty(false); return }
    if (!detail || detail.id !== diagramId || loadedId === diagramId) return
    const doc = detail.data || {}
    setNodes((doc.nodes || []) as DiagramNode[])
    setEdges((doc.edges || []).map(decorateEdge))
    setSettings({ ...DEFAULT_SETTINGS, ...(doc.settings || {}) })
    past.current = []; future.current = []; bumpHistory(x => x + 1)
    setDirty(false)
    setLoadedId(diagramId)
    setEditing(canEdit && !(doc.nodes || []).length)
    requestAnimationFrame(() => {
      if (doc.viewport) rf.setViewport(doc.viewport)
      else rf.fitView({ padding: 0.15, maxZoom: 1.2 })
    })
  }, [diagramId, detail, loadedId, canEdit, rf, setNodes, setEdges])

  // Older diagrams showed the asset name (often identical to the code) under linked devices —
  // swap those auto-generated second lines for the asset's model once per load.
  const modelSynced = useRef('')
  useEffect(() => {
    if (!loadedId || modelSynced.current === loadedId || !siteAssets.length) return
    modelSynced.current = loadedId
    setNodes(ns => ns.map(n => {
      if (n.type !== 'device') return n
      const d = n.data as DeviceData
      const a = d.assetId ? assetMap.get(d.assetId) : undefined
      if (!a || (d.sublabel && d.sublabel !== a.name && d.sublabel !== a.assetCode)) return n
      const sub = assetModel(a)
      return sub === d.sublabel ? n : ({ ...n, data: { ...d, sublabel: sub } } as DiagramNode)
    }))
  }, [loadedId, siteAssets, assetMap, setNodes])
  const syncFromAssets = () => {
    takeSnapshot()
    setNodes(ns => ns.map(n => {
      const d = n.data as DeviceData
      const a = n.type === 'device' && d.assetId ? assetMap.get(d.assetId) : undefined
      return a ? ({ ...n, data: { ...d, ...assetNodeData(a), showAssetCode: d.showAssetCode, ip: a.ipAddress || d.ip } } as DiagramNode) : n
    }))
    setDirty(true)
  }

  const applyAssetImages = (on: boolean) => {
    takeSnapshot()
    setNodes(ns => ns.map(n => {
      const d = n.data as DeviceData
      const a = n.type === 'device' && d.assetId ? assetMap.get(d.assetId) : undefined
      if (!a) return n
      if (on && a.images?.[0]) return { ...n, data: { ...d, image: a.images[0], shape: 'plain', w: undefined } } as DiagramNode
      if (!on && d.image && a.images?.includes(d.image)) return { ...n, data: { ...d, image: null } } as DiagramNode
      return n
    }))
    setDirty(true)
  }

  const confirmDiscard = () => !dirty || confirm('มีการแก้ไขที่ยังไม่ได้บันทึก — ต้องการทิ้งการแก้ไขหรือไม่?')
  const switchSite = (id: string) => { if (id !== siteId && confirmDiscard()) { setDirty(false); setSiteId(id); setDiagramId('') } }
  const switchDiagram = (id: string) => { if (id !== diagramId && confirmDiscard()) { setDirty(false); setDiagramId(id) } }

  useEffect(() => {
    if (!dirty) return
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', h)
    return () => window.removeEventListener('beforeunload', h)
  }, [dirty])

  // ── Mutations ──
  const buildDoc = (): DiagramDoc => ({ nodes: serializeNodes(nodesRef.current), edges: serializeEdges(edgesRef.current), viewport: rf.getViewport(), settings })
  const saveMut = useMutation({
    mutationFn: () => api.put(`/network-diagrams/${diagramId}`, { data: buildDoc() }).then(r => r.data),
    onSuccess: (d: DiagramFull) => {
      setDirty(false)
      qc.setQueryData(['network-diagram', d.id], d)
      qc.invalidateQueries({ queryKey: ['network-diagrams', siteId] })
    },
    onError: (e: any) => alert(e.response?.data?.message || 'บันทึกไม่สำเร็จ'),
  })
  const [modal, setModal] = useState<null | 'new' | 'rename'>(null)
  const createMut = useMutation({
    mutationFn: (v: { name: string; description: string; autoImport: boolean }) => {
      const data: DiagramDoc = { nodes: v.autoImport ? assetsToNodes(siteAssets, 0, 0) : [], edges: [], settings: DEFAULT_SETTINGS }
      return api.post('/network-diagrams', { siteId, name: v.name, description: v.description, data }).then(r => r.data)
    },
    onSuccess: (d: DiagramFull) => {
      qc.setQueryData(['network-diagram', d.id], d)
      qc.invalidateQueries({ queryKey: ['network-diagrams', siteId] })
      setModal(null); setDirty(false); setDiagramId(d.id)
    },
    onError: (e: any) => alert(e.response?.data?.message || 'สร้างไม่สำเร็จ'),
  })
  const renameMut = useMutation({
    mutationFn: (v: { name: string; description: string }) => api.put(`/network-diagrams/${diagramId}`, { name: v.name, description: v.description }).then(r => r.data),
    onSuccess: (d: DiagramFull) => {
      qc.setQueryData(['network-diagram', d.id], (old: DiagramFull | undefined) => old ? { ...old, name: d.name, description: d.description } : d)
      qc.invalidateQueries({ queryKey: ['network-diagrams', siteId] })
      setModal(null)
    },
    onError: (e: any) => alert(e.response?.data?.message || 'บันทึกไม่สำเร็จ'),
  })
  const duplicateMut = useMutation({
    mutationFn: () => api.post(`/network-diagrams/${diagramId}/duplicate`, {}).then(r => r.data),
    onSuccess: (d: DiagramFull) => {
      qc.invalidateQueries({ queryKey: ['network-diagrams', siteId] })
      setDirty(false); setDiagramId(d.id)
    },
  })
  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/network-diagrams/${diagramId}`),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['network-diagram', diagramId] })
      setDirty(false); setDiagramId('')
      qc.invalidateQueries({ queryKey: ['network-diagrams', siteId] })
    },
  })
  const save = useCallback(() => { if (diagramId && canEdit && !saveMut.isPending) saveMut.mutate() }, [diagramId, canEdit, saveMut])

  // ── Change handlers (track dirty; ignore pure selection / measurement changes) ──
  const resizingRef = useRef(false)
  const onNodesChange = useCallback((changes: NodeChange<DiagramNode>[]) => {
    if (changes.some(c => c.type !== 'select' && !(c.type === 'dimensions' && !c.resizing))) setDirty(true)
    if (changes.some(c => c.type === 'dimensions' && c.resizing)) {
      // snapshot once at the start of a resize gesture
      if (!resizingRef.current) { resizingRef.current = true; takeSnapshot() }
    } else if (changes.some(c => c.type === 'dimensions')) resizingRef.current = false
    onNodesChangeRaw(changes)
  }, [onNodesChangeRaw, takeSnapshot])
  const onEdgesChange = useCallback((changes: EdgeChange<DiagramEdge>[]) => {
    if (changes.some(c => c.type !== 'select')) setDirty(true)
    onEdgesChangeRaw(changes)
  }, [onEdgesChangeRaw])

  const newEdgeFor = useCallback((c: Connection): DiagramEdge => {
    const preset = EDGE_PRESETS.find(p => p.key === settings.edgePreset) || EDGE_PRESETS[0]
    return decorateEdge({ ...c, id: uid('e'), type: settings.edgeType, data: { ...preset.data, preset: preset.key } } as DiagramEdge)
  }, [settings.edgePreset, settings.edgeType])

  const onConnect = useCallback((c: Connection) => {
    if (c.source === c.target) return
    takeSnapshot()
    setNodes(ns => ns.some(n => n.selected) ? ns.map(n => ({ ...n, selected: false })) : ns)
    setEdges(es => addEdge({ ...newEdgeFor(c), selected: true } as DiagramEdge, es.map(e => ({ ...e, selected: false } as DiagramEdge))))
    setDirty(true)
  }, [newEdgeFor, setNodes, setEdges, takeSnapshot])
  const onReconnect = useCallback((oldEdge: DiagramEdge, c: Connection) => {
    takeSnapshot()
    setEdges(es => reconnectEdge(oldEdge, c, es))
    setDirty(true)
  }, [setEdges, takeSnapshot])
  const onBeforeDelete = useCallback(async () => { takeSnapshot(); return true }, [takeSnapshot])

  // Dragging a zone carries the nodes sitting inside it
  const zoneDrag = useRef(new Map<string, { start: { x: number; y: number }; kids: { id: string; x: number; y: number }[] }>())
  const onNodeDragStart: OnNodeDrag<DiagramNode> = useCallback((_e, _n, dragged) => {
    takeSnapshot()
    zoneDrag.current.clear()
    const draggedIds = new Set(dragged.map(d => d.id))
    for (const z of dragged) {
      if (z.type !== 'zone') continue
      const zx = z.position.x, zy = z.position.y, zw = nodeW(z), zh = nodeH(z)
      const kids = nodesRef.current.filter(n => {
        if (draggedIds.has(n.id) || n.id === z.id) return false
        const cx = n.position.x + nodeW(n) / 2, cy = n.position.y + nodeH(n) / 2
        return cx >= zx && cx <= zx + zw && cy >= zy && cy <= zy + zh
      }).map(n => ({ id: n.id, x: n.position.x, y: n.position.y }))
      zoneDrag.current.set(z.id, { start: { ...z.position }, kids })
    }
  }, [takeSnapshot])
  const onNodeDrag: OnNodeDrag<DiagramNode> = useCallback((_e, _n, dragged) => {
    if (!zoneDrag.current.size) return
    const moves = new Map<string, { x: number; y: number }>()
    for (const z of dragged) {
      const info = zoneDrag.current.get(z.id)
      if (!info) continue
      const dx = z.position.x - info.start.x, dy = z.position.y - info.start.y
      for (const k of info.kids) moves.set(k.id, { x: k.x + dx, y: k.y + dy })
    }
    if (moves.size) setNodes(ns => ns.map(n => moves.has(n.id) ? { ...n, position: moves.get(n.id)! } : n))
  }, [setNodes])
  const onNodeDragStop = useCallback(() => { zoneDrag.current.clear() }, [])

  // ── Mutating helpers ──
  const updateNodeData = useCallback((id: string, patch: Partial<DeviceData> | Partial<ZoneData> | Partial<TextData>) => {
    setNodes(ns => ns.map(n => n.id === id ? ({ ...n, data: { ...n.data, ...patch } } as DiagramNode) : n))
    setDirty(true)
  }, [setNodes])
  const updateNode = useCallback((id: string, patch: Partial<DiagramNode>) => {
    setNodes(ns => ns.map(n => n.id === id ? ({ ...n, ...patch } as DiagramNode) : n))
    setDirty(true)
  }, [setNodes])
  const updateEdge = useCallback((id: string, patch: Partial<DiagramEdge>, dataPatch?: Partial<EdgeData>) => {
    setEdges(es => es.map(e => e.id === id ? decorateEdge({ ...e, ...patch, data: { ...e.data, ...dataPatch } }) : e))
    setDirty(true)
  }, [setEdges])

  const addNodes = useCallback((list: DiagramNode[]) => {
    takeSnapshot()
    setNodes(ns => [...ns.map(n => ({ ...n, selected: false })), ...list.map(n => ({ ...n, selected: list.length === 1 }))])
    setEdges(es => es.map(e => ({ ...e, selected: false })))
    setDirty(true)
  }, [setNodes, setEdges, takeSnapshot])

  const makeNode = (spec: { type: string; kind?: string; assetId?: string }, pos: { x: number; y: number }): DiagramNode | null => {
    if (spec.type === 'zone') return { id: uid('z'), type: 'zone', position: pos, width: 360, height: 240, zIndex: -1, data: { label: 'ZONE', fill: '#fde68a', border: '#78716c', borderStyle: 'dashed', opacity: 0.55 } }
    if (spec.type === 'text') return { id: uid('t'), type: 'text', position: pos, data: { text: 'ข้อความ', fontSize: 16 } }
    if (spec.type === 'asset') {
      const a = assetMap.get(spec.assetId || '')
      if (!a) return null
      return { id: uid('n'), type: 'device', position: pos, data: { kind: guessKind(a), ...assetNodeData(a), ip: a.ipAddress || undefined } }
    }
    const k = KIND_MAP[spec.kind || 'generic'] || KIND_MAP.generic
    return { id: uid('n'), type: 'device', position: pos, data: { kind: k.kind, label: k.label } }
  }
  const viewCenter = () => {
    const el = wrapperRef.current?.querySelector('.react-flow') as HTMLElement | null
    const r = el?.getBoundingClientRect()
    const p = rf.screenToFlowPosition({ x: (r?.left ?? 0) + (r?.width ?? 800) / 2, y: (r?.top ?? 0) + (r?.height ?? 600) / 2 })
    return { x: p.x - 24 + (Math.random() * 40 - 20), y: p.y - 24 + (Math.random() * 40 - 20) }
  }
  const addAtCenter = (spec: { type: string; kind?: string; assetId?: string }) => {
    const n = makeNode(spec, viewCenter())
    if (n) addNodes([n])
  }
  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const raw = e.dataTransfer.getData('application/x-netdiagram')
    if (!raw) return
    const pos = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY })
    const n = makeNode(JSON.parse(raw), { x: pos.x - 24, y: pos.y - 24 })
    if (n) addNodes([n])
  }

  // ── Clipboard ──
  const clipboard = useRef<{ nodes: DiagramNode[]; edges: DiagramEdge[] } | null>(null)
  const copySel = useCallback(() => {
    const sel = nodesRef.current.filter(n => n.selected)
    if (!sel.length) return
    const ids = new Set(sel.map(n => n.id))
    clipboard.current = { nodes: JSON.parse(JSON.stringify(serializeNodes(sel))), edges: JSON.parse(JSON.stringify(serializeEdges(edgesRef.current.filter(e => ids.has(e.source) && ids.has(e.target))))) }
  }, [])
  const paste = useCallback(() => {
    const cb = clipboard.current
    if (!cb) return
    takeSnapshot()
    const idMap = new Map<string, string>()
    const ns = cb.nodes.map(n => { const id = uid(n.type === 'zone' ? 'z' : 'n'); idMap.set(n.id, id); return { ...n, id, selected: true, position: { x: n.position.x + 40, y: n.position.y + 40 } } as DiagramNode })
    const es = cb.edges.map(e => decorateEdge({ ...e, id: uid('e'), source: idMap.get(e.source)!, target: idMap.get(e.target)! }))
    clipboard.current = { nodes: ns.map(n => ({ ...n, selected: false })), edges: es }
    setNodes(cur => [...cur.map(n => ({ ...n, selected: false })), ...ns])
    setEdges(cur => [...cur.map(e => ({ ...e, selected: false })), ...es])
    setDirty(true)
  }, [setNodes, setEdges, takeSnapshot])
  const duplicateSel = useCallback(() => { copySel(); paste() }, [copySel, paste])
  const deleteSel = useCallback(() => {
    const ns = nodesRef.current.filter(n => n.selected), es = edgesRef.current.filter(e => e.selected)
    if (ns.length || es.length) { takeSnapshot(); rf.deleteElements({ nodes: ns, edges: es }) }
  }, [rf, takeSnapshot])

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return
      const mod = e.ctrlKey || e.metaKey
      if (mod && e.key.toLowerCase() === 's') { e.preventDefault(); if (dirty) save(); return }
      if (e.key === 'Escape' && fullscreen) { setFullscreen(false); return }
      if (!isEditing || !mod) return
      const k = e.key.toLowerCase()
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); undo() }
      else if (k === 'y' || (k === 'z' && e.shiftKey)) { e.preventDefault(); redo() }
      else if (k === 'c') { copySel() }
      else if (k === 'v') { e.preventDefault(); paste() }
      else if (k === 'd') { e.preventDefault(); duplicateSel() }
      else if (k === 'a') { e.preventDefault(); setNodes(ns => ns.map(n => ({ ...n, selected: true }))) }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [isEditing, dirty, fullscreen, save, undo, redo, copySel, paste, duplicateSel, setNodes])

  // ── Selection-derived ──
  const selNodes = nodes.filter(n => n.selected)
  const selEdges = edges.filter(e => e.selected)
  const one = selNodes.length === 1 && selEdges.length === 0 ? selNodes[0] : null
  const oneEdge = selEdges.length === 1 && selNodes.length === 0 ? selEdges[0] : null

  // ── Align / distribute / z-order ──
  const align = (mode: 'l' | 'c' | 'r' | 't' | 'm' | 'b') => {
    if (selNodes.length < 2) return
    takeSnapshot()
    const xs = selNodes.map(n => n.position.x), ys = selNodes.map(n => n.position.y)
    const rs = selNodes.map(n => n.position.x + nodeW(n)), bs = selNodes.map(n => n.position.y + nodeH(n))
    const minX = Math.min(...xs), maxR = Math.max(...rs), minY = Math.min(...ys), maxB = Math.max(...bs)
    const cx = (minX + maxR) / 2, cy = (minY + maxB) / 2
    const ids = new Set(selNodes.map(n => n.id))
    setNodes(ns => ns.map(n => {
      if (!ids.has(n.id)) return n
      const w = nodeW(n), h = nodeH(n), p = { ...n.position }
      if (mode === 'l') p.x = minX; if (mode === 'r') p.x = maxR - w; if (mode === 'c') p.x = cx - w / 2
      if (mode === 't') p.y = minY; if (mode === 'b') p.y = maxB - h; if (mode === 'm') p.y = cy - h / 2
      return { ...n, position: p }
    }))
    setDirty(true)
  }
  const distribute = (axis: 'x' | 'y') => {
    if (selNodes.length < 3) return
    takeSnapshot()
    const size = (n: DiagramNode) => axis === 'x' ? nodeW(n) : nodeH(n)
    const sorted = [...selNodes].sort((a, b) => (a.position[axis] + size(a) / 2) - (b.position[axis] + size(b) / 2))
    const first = sorted[0].position[axis] + size(sorted[0]) / 2
    const last = sorted[sorted.length - 1].position[axis] + size(sorted[sorted.length - 1]) / 2
    const step = (last - first) / (sorted.length - 1)
    const target = new Map(sorted.map((n, i) => [n.id, first + step * i - size(n) / 2]))
    setNodes(ns => ns.map(n => target.has(n.id) ? { ...n, position: { ...n.position, [axis]: target.get(n.id)! } } : n))
    setDirty(true)
  }
  const zOrder = (dir: 1 | -1) => {
    if (!selNodes.length) return
    takeSnapshot()
    const zs = nodes.map(n => n.zIndex ?? 0)
    const z = dir === 1 ? Math.max(...zs) + 1 : Math.min(...zs) - 1
    const ids = new Set(selNodes.map(n => n.id))
    setNodes(ns => ns.map(n => ids.has(n.id) ? { ...n, zIndex: z } : n))
    setDirty(true)
  }

  // ── Search ──
  const [find, setFind] = useState('')
  const findIdx = useRef(0)
  const doFind = () => {
    const q = find.trim().toLowerCase()
    if (!q) return
    const hits = nodes.filter(n => {
      const d: any = n.data
      return [d.label, d.sublabel, d.ip, d.text, d.assetCode, d.details].some((v: any) => typeof v === 'string' && v.toLowerCase().includes(q))
    })
    if (!hits.length) { alert('ไม่พบอุปกรณ์ที่ค้นหา'); return }
    const hit = hits[findIdx.current++ % hits.length]
    setNodes(ns => ns.map(n => ({ ...n, selected: n.id === hit.id })))
    setEdges(es => es.map(e => ({ ...e, selected: false })))
    rf.fitView({ nodes: [{ id: hit.id }], duration: 400, maxZoom: 1.5 })
  }

  // ── Export / import ──
  const fileName = `${site?.name || 'site'}-${detail?.name || 'diagram'}`.replace(/[\\/:*?"<>|]+/g, '_')
  const exportPng = async () => {
    if (!nodes.length) return
    const vpEl = wrapperRef.current?.querySelector('.react-flow__viewport') as HTMLElement | null
    if (!vpEl) return
    const bounds = getNodesBounds(nodes)
    const w = Math.max(400, Math.ceil(bounds.width + 120)), h = Math.max(300, Math.ceil(bounds.height + 120))
    const vp = getViewportForBounds(bounds, w, h, 0.2, 2, 0.05)
    try {
      const url = await toPng(vpEl, {
        backgroundColor: settings.bg, width: w, height: h, pixelRatio: 2,
        style: { width: `${w}px`, height: `${h}px`, transform: `translate(${vp.x}px, ${vp.y}px) scale(${vp.zoom})` },
      })
      const a = document.createElement('a'); a.href = url; a.download = `${fileName}.png`; a.click()
    } catch { alert('Export รูปภาพไม่สำเร็จ') }
  }
  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ format: 'it-network-diagram', version: 1, name: detail?.name, site: site?.name, ...buildDoc() }, null, 2)], { type: 'application/json' })
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${fileName}.json`; a.click()
    setTimeout(() => URL.revokeObjectURL(a.href), 1000)
  }
  const importRef = useRef<HTMLInputElement>(null)
  const importJson = async (f: File | undefined) => {
    if (!f) return
    try {
      const doc = JSON.parse(await f.text()) as DiagramDoc
      if (!Array.isArray(doc.nodes) || !Array.isArray(doc.edges ?? [])) throw new Error()
      if (nodes.length && !confirm('นำเข้าไฟล์จะแทนที่ Diagram ปัจจุบันทั้งหมด — ดำเนินการต่อ?')) return
      takeSnapshot()
      setNodes(doc.nodes); setEdges((doc.edges || []).map(decorateEdge))
      if (doc.settings) setSettings({ ...DEFAULT_SETTINGS, ...doc.settings })
      setDirty(true)
      requestAnimationFrame(() => rf.fitView({ padding: 0.15 }))
    } catch { alert('ไฟล์ไม่ถูกต้อง (ต้องเป็นไฟล์ JSON ที่ Export จาก Network Diagram)') }
    if (importRef.current) importRef.current.value = ''
  }
  const importAllAssets = () => {
    const onDiagram = new Set(nodes.map(n => (n.data as DeviceData).assetId).filter(Boolean))
    const remaining = siteAssets.filter(a => !onDiagram.has(a.id))
    if (!remaining.length) { alert('Assets ทั้งหมดของ Site นี้อยู่ใน Diagram แล้ว'); return }
    if (!confirm(`เพิ่ม ${remaining.length} อุปกรณ์จาก Assets ของ Site นี้?`)) return
    const b = nodes.length ? getNodesBounds(nodes) : { x: 0, y: 0, width: 0, height: 0 }
    const list = assetsToNodes(remaining, b.x, nodes.length ? b.y + b.height + 120 : 0)
    addNodes(list)
    requestAnimationFrame(() => rf.fitView({ padding: 0.15, duration: 300 }))
  }

  const deviceCount = nodes.filter(n => n.type === 'device').length
  const linkedCount = nodes.filter(n => n.type === 'device' && (n.data as DeviceData).assetId).length
  const canUndo = past.current.length > 0, canRedo = future.current.length > 0

  // ─────────────────────────────────────────────────────────────────────────
  if (sitesLoading) return <div className="h-full flex items-center justify-center text-gray-400 gap-2"><RefreshCw size={16} className="animate-spin"/> กำลังโหลด...</div>
  if (!sites.length) return (
    <div className="h-full flex flex-col items-center justify-center text-gray-400 gap-2 p-6 text-center">
      <MapPin size={36} className="text-gray-300"/>
      <p className="text-sm">ยังไม่มี Site — เพิ่ม Site ได้ที่ <Link to="/masterdata" className="text-blue-600 hover:underline">Master Data</Link></p>
    </div>
  )

  return (
    <div ref={wrapperRef} className={fullscreen ? 'fixed inset-0 z-50 bg-white flex flex-col' : 'h-full flex flex-col bg-white'}>
      {/* ── Top bar ── */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-gray-200 bg-white flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <MapPin size={14} className="text-gray-400"/>
          <select value={siteId} onChange={e => switchSite(e.target.value)} className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white max-w-[220px]">
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}{s.company?.name ? ` — ${s.company.name}` : ''}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <Network size={14} className="text-gray-400"/>
          <select value={diagramId} onChange={e => switchDiagram(e.target.value)} disabled={!diagrams.length}
            className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white max-w-[240px] disabled:text-gray-400">
            {!diagrams.length && <option value="">— ยังไม่มี Diagram —</option>}
            {diagrams.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          {canEdit && (
            <>
              <TbBtn title="สร้าง Diagram ใหม่" onClick={() => { if (confirmDiscard()) setModal('new') }}><Plus size={16}/></TbBtn>
              {diagramId && <>
                <TbBtn title="แก้ไขชื่อ / คำอธิบาย" onClick={() => setModal('rename')}><Pencil size={15}/></TbBtn>
                <TbBtn title="ทำสำเนา Diagram (สำเนาจากเวอร์ชันที่บันทึกล่าสุด)" onClick={() => { if (confirmDiscard()) duplicateMut.mutate() }}><Copy size={15}/></TbBtn>
                <TbBtn title="ลบ Diagram" onClick={() => { if (confirm(`ลบ Diagram "${detail?.name}" ?`)) deleteMut.mutate() }}><Trash2 size={15}/></TbBtn>
              </>}
            </>
          )}
        </div>

        {diagramId && (
          <>
            <div className="w-px h-6 bg-gray-200 mx-1"/>
            {canEdit && (
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button onClick={() => setEditing(false)} className={`flex items-center gap-1 px-2.5 py-1 text-xs ${!editing ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}><Eye size={13}/> ดู</button>
                <button onClick={() => setEditing(true)} className={`flex items-center gap-1 px-2.5 py-1 text-xs ${editing ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}><Pencil size={13}/> แก้ไข</button>
              </div>
            )}
            {isEditing && (
              <div className="flex items-center">
                <TbBtn title="Undo (Ctrl+Z)" onClick={undo} disabled={!canUndo}><Undo2 size={16}/></TbBtn>
                <TbBtn title="Redo (Ctrl+Y)" onClick={redo} disabled={!canRedo}><Redo2 size={16}/></TbBtn>
                <TbBtn title="ทำซ้ำรายการที่เลือก (Ctrl+D)" onClick={duplicateSel} disabled={!selNodes.length}><Copy size={16}/></TbBtn>
                <TbBtn title="ลบรายการที่เลือก (Delete)" onClick={deleteSel} disabled={!selNodes.length && !selEdges.length}><Trash2 size={16}/></TbBtn>
                <TbBtn title="Snap to grid" onClick={() => { setSettings(s => ({ ...s, snap: !s.snap })); setDirty(true) }} active={settings.snap}><Magnet size={16}/></TbBtn>
              </div>
            )}
            <div className="flex items-center gap-1 ml-auto">
              <div className="relative">
                <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"/>
                <input value={find} onChange={e => { setFind(e.target.value); findIdx.current = 0 }} onKeyDown={e => { if (e.key === 'Enter') doFind() }}
                  placeholder="ค้นหาอุปกรณ์ / IP" className="pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-lg w-40"/>
              </div>
              <TbBtn title="Export รูปภาพ PNG" onClick={exportPng} disabled={!nodes.length}><ImageIcon size={16}/></TbBtn>
              <TbBtn title="Export ไฟล์ JSON" onClick={exportJson}><Download size={16}/></TbBtn>
              {isEditing && <TbBtn title="Import ไฟล์ JSON" onClick={() => importRef.current?.click()}><Upload size={16}/></TbBtn>}
              <input ref={importRef} type="file" accept=".json,application/json" className="hidden" onChange={e => importJson(e.target.files?.[0])}/>
              <TbBtn title={fullscreen ? 'ออกจากเต็มจอ (Esc)' : 'เต็มจอ'} onClick={() => setFullscreen(f => !f)}>{fullscreen ? <Minimize2 size={16}/> : <Maximize2 size={16}/>}</TbBtn>
              {canEdit && (
                <button onClick={save} disabled={!dirty || saveMut.isPending}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ml-1 ${dirty ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-400'}`}>
                  {saveMut.isPending ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>}
                  {dirty ? 'บันทึก' : 'บันทึกแล้ว'}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Body ── */}
      {!diagramId ? (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-3 p-6 text-center">
          <Network size={44} className="text-gray-300"/>
          <p className="text-sm">Site <b className="text-gray-600">{site?.name}</b> ยังไม่มี Network Diagram</p>
          {canEdit
            ? <button onClick={() => setModal('new')} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700"><Plus size={15}/> สร้าง Network Diagram</button>
            : <p className="text-xs">ติดต่อผู้ดูแลระบบเพื่อสร้าง Diagram</p>}
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex">
          {isEditing && <Palette siteAssets={siteAssets} nodes={nodes} onAdd={addAtCenter} onImportAll={importAllAssets}/>}

          <div className="flex-1 min-w-0 relative" onDragOver={onDragOver} onDrop={onDrop}>
            {detailLoading && loadedId !== diagramId && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 text-gray-400 gap-2"><RefreshCw size={16} className="animate-spin"/> กำลังโหลด Diagram...</div>
            )}
            <DiagramActionsContext.Provider value={diagramActions}>
            <EditModeContext.Provider value={isEditing}>
              <ReactFlow<DiagramNode, DiagramEdge>
                nodes={nodes} edges={edges} nodeTypes={nodeTypes} edgeTypes={edgeTypes}
                onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
                onConnect={onConnect} onReconnect={onReconnect} onBeforeDelete={onBeforeDelete}
                onNodeDragStart={onNodeDragStart} onNodeDrag={onNodeDrag} onNodeDragStop={onNodeDragStop}
                connectionMode={ConnectionMode.Loose}
                nodesDraggable={isEditing} nodesConnectable={isEditing} edgesReconnectable={isEditing}
                elementsSelectable deleteKeyCode={isEditing ? ['Delete', 'Backspace'] : null}
                multiSelectionKeyCode={['Shift', 'Control', 'Meta']} selectionKeyCode={isEditing ? 'Shift' : null}
                elevateNodesOnSelect={false} elevateEdgesOnSelect={false}
                snapToGrid={settings.snap} snapGrid={[10, 10]}
                minZoom={0.05} maxZoom={4}
                connectionLineStyle={{ stroke: '#2563eb', strokeWidth: 2 }}
                style={{ background: settings.bg }}
                proOptions={{ hideAttribution: true }}
                className={isEditing ? 'nd-canvas-editing' : ''}
              >
                {settings.grid !== 'none' && (
                  <Background gap={settings.gridGap}
                    variant={settings.grid === 'lines' ? BackgroundVariant.Lines : settings.grid === 'cross' ? BackgroundVariant.Cross : BackgroundVariant.Dots}
                    color={settings.grid === 'dots' ? '#cbd5e1' : '#e2e8f0'}/>
                )}
                <Controls showInteractive={false}/>
                {settings.minimap && <MiniMap pannable zoomable nodeColor={n => n.type === 'zone' ? ((n.data as ZoneData).fill || '#fde68a') : n.type === 'device' ? ((n.data as DeviceData).color || KIND_MAP[(n.data as DeviceData).kind]?.color || '#6b7280') : '#9ca3af'} className="!bg-white/90"/>}
                {settings.stats && (
                  <Panel position="bottom-left" className="!ml-14 !mb-3">
                    <div className="bg-white/95 border border-gray-300 rounded-md px-3 py-1.5 text-[11px] text-gray-700 shadow-sm">
                      <div className="font-semibold">Stats:</div>
                      <div>Devices: {deviceCount} · Links: {edges.length}</div>
                      <div>ผูก Asset แล้ว: {linkedCount}</div>
                    </div>
                  </Panel>
                )}
              </ReactFlow>
            </EditModeContext.Provider>
            </DiagramActionsContext.Provider>
          </div>

          {/* ── Right panel ── */}
          <div className="w-72 flex-shrink-0 border-l border-gray-200 bg-white overflow-y-auto"
            onPointerDownCapture={isEditing ? takeSnapshot : undefined} onFocusCapture={isEditing ? takeSnapshot : undefined}>
            {one?.type === 'device' && (isEditing
              ? <DevicePanel node={one} assets={siteAssets} update={p => updateNodeData(one.id, p)} onZ={zOrder}/>
              : <DeviceInfo data={one.data as DeviceData} asset={assetMap.get((one.data as DeviceData).assetId || '')}/>)}
            {one?.type === 'zone' && isEditing && <ZonePanel data={one.data as ZoneData} update={p => updateNodeData(one.id, p)} onZ={zOrder}/>}
            {one?.type === 'text' && isEditing && <TextPanel data={one.data as TextData} update={p => updateNodeData(one.id, p)} onZ={zOrder}/>}
            {oneEdge && isEditing && <EdgePanel edge={oneEdge} update={(p, d) => updateEdge(oneEdge.id, p, d)} nodes={nodes}/>}
            {oneEdge && !isEditing && <EdgeInfo edge={oneEdge} nodes={nodes}/>}
            {isEditing && selNodes.length > 1 && (
              <MultiPanel count={selNodes.length} align={align} distribute={distribute} onZ={zOrder}
                setKind={(k) => { selNodes.filter(n => n.type === 'device').forEach(n => updateNodeData(n.id, { kind: k, color: undefined })) }}/>
            )}
            {!selNodes.length && !selEdges.length && (
              <OverviewPanel detail={detail} site={site} editing={isEditing} settings={settings}
                setSettings={p => { setSettings(s => ({ ...s, ...p })); setDirty(true) }}
                deviceCount={deviceCount} linkCount={edges.length} linkedCount={linkedCount} onSyncAssets={syncFromAssets} onAssetImages={applyAssetImages}/>
            )}
            {!isEditing && (one?.type === 'zone' || one?.type === 'text') && (
              <div className="p-4 text-xs text-gray-400">{one.type === 'zone' ? `Zone: ${(one.data as ZoneData).label || '-'}` : (one.data as TextData).text}</div>
            )}
          </div>
        </div>
      )}

      {modal && (
        <DiagramModal isNew={modal === 'new'} busy={createMut.isPending || renameMut.isPending}
          initial={modal === 'rename' ? { name: detail?.name || '', description: detail?.description || '' } : { name: '', description: '' }}
          onClose={() => setModal(null)}
          onSubmit={v => modal === 'new' ? createMut.mutate(v) : renameMut.mutate(v)}/>
      )}
    </div>
  )
}

// ─── Left palette ────────────────────────────────────────────────────────────
function Palette({ siteAssets, nodes, onAdd, onImportAll }: {
  siteAssets: SiteAsset[]; nodes: DiagramNode[]
  onAdd: (spec: { type: string; kind?: string; assetId?: string }) => void; onImportAll: () => void
}) {
  const [tab, setTab] = useState<'shapes' | 'assets'>('shapes')
  const [q, setQ] = useState('')
  const onDiagram = useMemo(() => new Set(nodes.map(n => (n.data as DeviceData).assetId).filter(Boolean)), [nodes])
  const drag = (spec: object) => (e: React.DragEvent) => {
    e.dataTransfer.setData('application/x-netdiagram', JSON.stringify(spec))
    e.dataTransfer.effectAllowed = 'move'
  }
  const filtered = siteAssets.filter(a => {
    const s = q.trim().toLowerCase()
    return !s || [a.assetCode, a.name, a.type, a.ipAddress, a.brand, a.model].some(v => v && v.toLowerCase().includes(s))
  })
  return (
    <div className="w-60 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col min-h-0">
      <div className="flex border-b border-gray-200 text-xs">
        <button onClick={() => setTab('shapes')} className={`flex-1 py-2 font-medium border-b-2 -mb-px ${tab === 'shapes' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}>อุปกรณ์ / รูปร่าง</button>
        <button onClick={() => setTab('assets')} className={`flex-1 py-2 font-medium border-b-2 -mb-px ${tab === 'assets' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}>Assets ({siteAssets.length})</button>
      </div>
      {tab === 'shapes' ? (
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          <p className="text-[11px] text-gray-400">ลากไปวางบน Diagram หรือคลิกเพื่อเพิ่มกลางจอ</p>
          <div>
            <div className="text-[11px] font-semibold text-gray-500 mb-1.5">รูปร่าง</div>
            <div className="grid grid-cols-2 gap-1.5">
              <button draggable onDragStart={drag({ type: 'zone' })} onClick={() => onAdd({ type: 'zone' })}
                className="flex items-center gap-1.5 px-2 py-2 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 text-xs text-gray-700 cursor-grab">
                <Square size={15} className="text-amber-500"/> Zone / กลุ่ม
              </button>
              <button draggable onDragStart={drag({ type: 'text' })} onClick={() => onAdd({ type: 'text' })}
                className="flex items-center gap-1.5 px-2 py-2 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 text-xs text-gray-700 cursor-grab">
                <Type size={15} className="text-gray-500"/> ข้อความ
              </button>
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-gray-500 mb-1.5">อุปกรณ์เครือข่าย</div>
            <div className="grid grid-cols-3 gap-1.5">
              {DEVICE_KINDS.map(k => {
                const Icon = k.icon
                return (
                  <button key={k.kind} draggable onDragStart={drag({ type: 'device', kind: k.kind })} onClick={() => onAdd({ type: 'device', kind: k.kind })}
                    title={k.label}
                    className="flex flex-col items-center gap-1 px-1 py-2 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 cursor-grab">
                    <span className="w-8 h-8 rounded-md flex items-center justify-center text-white" style={{ background: k.color }}><Icon size={17}/></span>
                    <span className="text-[10px] leading-tight text-gray-600 text-center line-clamp-2">{k.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="p-2 space-y-2 border-b border-gray-100">
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"/>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="ค้นหา code, ชื่อ, IP" className={inputCls + ' pl-7'}/>
            </div>
            <button onClick={onImportAll} disabled={!siteAssets.length}
              className="w-full px-2 py-1.5 text-xs rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50">
              + เพิ่ม Assets ที่ยังไม่อยู่ใน Diagram ทั้งหมด
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {!filtered.length && <p className="text-xs text-gray-400 text-center py-6">ไม่พบ Asset ใน Site นี้</p>}
            {filtered.map(a => {
              const k = KIND_MAP[guessKind(a)]
              const Icon = k.icon
              const used = onDiagram.has(a.id)
              return (
                <button key={a.id} draggable onDragStart={drag({ type: 'asset', assetId: a.id })} onClick={() => onAdd({ type: 'asset', assetId: a.id })}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg border text-left cursor-grab ${used ? 'border-green-200 bg-green-50/60' : 'border-gray-100 hover:border-blue-300 hover:bg-blue-50'}`}>
                  {a.images?.[0]
                    ? <img src={a.images[0]} alt="" className="w-7 h-7 rounded flex-shrink-0 object-cover border border-gray-200 bg-white"/>
                    : <span className="w-7 h-7 rounded flex-shrink-0 flex items-center justify-center text-white" style={{ background: k.color }}><Icon size={14}/></span>}
                  <span className="min-w-0 flex-1">
                    <span className="block text-[11px] font-semibold text-gray-800 truncate">{a.assetCode || '-'} <span className="font-normal text-gray-400">· {a.type}</span></span>
                    <span className="block text-[10px] text-gray-500 truncate">{a.name}{a.ipAddress ? ` · ${a.ipAddress}` : ''}</span>
                  </span>
                  {used && <span className="text-[9px] text-green-700 flex-shrink-0">อยู่แล้ว</span>}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Right panels ────────────────────────────────────────────────────────────
function PanelHead({ title, children }: { title: string; children?: React.ReactNode }) {
  return <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between"><h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{title}</h3>{children}</div>
}
function ZButtons({ onZ }: { onZ: (d: 1 | -1) => void }) {
  return (
    <div className="flex gap-1">
      <TbBtn title="นำไปไว้ด้านหน้า" onClick={() => onZ(1)}><BringToFront size={14}/></TbBtn>
      <TbBtn title="นำไปไว้ด้านหลัง" onClick={() => onZ(-1)}><SendToBack size={14}/></TbBtn>
    </div>
  )
}

function DevicePanel({ node, assets, update, onZ }: { node: DiagramNode; assets: SiteAsset[]; update: (p: Partial<DeviceData>) => void; onZ: (d: 1 | -1) => void }) {
  const d = node.data as DeviceData
  const k = KIND_MAP[d.kind] || KIND_MAP.generic
  const [assetQ, setAssetQ] = useState('')
  const linked = assets.find(a => a.id === d.assetId)
  const matches = assetQ.trim()
    ? assets.filter(a => [a.assetCode, a.name, a.ipAddress, a.type].some(v => v && v.toLowerCase().includes(assetQ.trim().toLowerCase()))).slice(0, 30)
    : []
  return (
    <div>
      <PanelHead title="อุปกรณ์"><ZButtons onZ={onZ}/></PanelHead>
      <div className="p-4 space-y-3">
        <Field label="ประเภทอุปกรณ์">
          <select value={d.kind} onChange={e => update({ kind: e.target.value })} className={inputCls}>
            {DEVICE_KINDS.map(x => <option key={x.kind} value={x.kind}>{x.label}</option>)}
          </select>
        </Field>
        <Field label="ชื่อ / Hostname"><textarea rows={1} value={d.label || ''} onChange={e => update({ label: e.target.value })} className={inputCls}/></Field>
        <Field label="รุ่น / คำอธิบายรอง"><input value={d.sublabel || ''} onChange={e => update({ sublabel: e.target.value })} className={inputCls} placeholder="เช่น Cisco C9300-48P"/></Field>
        <Field label="IP Address"><input value={d.ip || ''} onChange={e => update({ ip: e.target.value })} className={inputCls + ' font-mono'} placeholder="10.10.1.1"/></Field>
        <Field label="รายละเอียดเพิ่มเติม (VLAN, Port ฯลฯ)"><textarea rows={2} value={d.details || ''} onChange={e => update({ details: e.target.value })} className={inputCls}/></Field>

        <div className="rounded-lg border border-gray-200 p-2.5 space-y-2">
          <div className="text-[11px] font-medium text-gray-500 flex items-center gap-1"><LinkIcon size={11}/> ผูกกับ Asset</div>
          {linked ? (
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0 text-xs">
                <Link to={`/assets/${linked.id}`} target="_blank" className="font-semibold text-blue-600 hover:underline">{linked.assetCode || linked.name}</Link>
                <div className="text-gray-500 truncate">{linked.name}</div>
              </div>
              <button onClick={() => update({ assetId: null, assetCode: null })} className="p-1 text-gray-400 hover:text-red-600" title="ยกเลิกการผูก"><X size={13}/></button>
            </div>
          ) : d.assetId ? (
            <div className="text-[11px] text-amber-600 flex items-center justify-between">ไม่พบ Asset ใน Site นี้ <button onClick={() => update({ assetId: null, assetCode: null })} className="underline">ล้าง</button></div>
          ) : null}
          <input value={assetQ} onChange={e => setAssetQ(e.target.value)} placeholder="ค้นหา Asset เพื่อผูก..." className={inputCls}/>
          {matches.length > 0 && (
            <div className="max-h-40 overflow-y-auto border border-gray-100 rounded-lg">
              {matches.map(a => (
                <button key={a.id} onClick={() => { update({ ...assetNodeData(a), showAssetCode: d.showAssetCode ?? false, ...(!d.ip && a.ipAddress ? { ip: a.ipAddress } : {}) }); setAssetQ('') }}
                  className="w-full text-left px-2 py-1 text-[11px] hover:bg-blue-50 border-b border-gray-50 last:border-0">
                  <b>{a.assetCode || '-'}</b> · {a.name}{a.ipAddress ? ` · ${a.ipAddress}` : ''}
                </button>
              ))}
            </div>
          )}
          {d.assetId && (
            <label className="flex items-center gap-1.5 text-[11px] text-gray-600">
              <input type="checkbox" checked={d.showAssetCode !== false} onChange={e => update({ showAssetCode: e.target.checked })}/> แสดง Asset Code บน Diagram
            </label>
          )}
        </div>

        <ImageSection d={d} asset={linked} update={update}/>

        <div className="pt-1 border-t border-gray-100"/>
        <Field label="ขนาดไอคอน / รูป">
          <Seg value={d.w ? ('' as any) : (d.size || 'md')} onChange={v => update({ size: v, w: undefined })} options={[{ key: 'sm', label: 'S' }, { key: 'md', label: 'M' }, { key: 'lg', label: 'L' }, { key: 'xl', label: 'XL' }, { key: '2xl', label: '2XL' }]}/>
        </Field>
        <Field label="ความกว้าง (px) — หรือลากมุมขวาล่างของไอคอน/รูปบน Diagram">
          <div className="flex items-center gap-2">
            <input type="range" min={20} max={600} value={deviceBoxWidth(d)} onChange={e => update({ w: +e.target.value })} className="flex-1"/>
            <input type="number" min={20} max={1600} value={deviceBoxWidth(d)} onChange={e => update({ w: Math.max(20, +e.target.value || 20) })} className={inputCls + ' !w-16'}/>
          </div>
        </Field>
        <Field label="รูปแบบไอคอน">
          <Seg value={d.shape || 'tile'} onChange={v => update({ shape: v })} options={[{ key: 'tile', label: 'กล่อง' }, { key: 'circle', label: 'วงกลม' }, { key: 'plain', label: 'ไอคอนเปล่า' }]}/>
        </Field>
        <Field label="ตำแหน่งข้อความ">
          <Seg value={d.labelPos || 'bottom'} onChange={v => update({ labelPos: v })} options={[{ key: 'bottom', label: 'ด้านล่าง' }, { key: 'right', label: 'ด้านขวา' }]}/>
        </Field>
        <ColorField label="สีไอคอน" value={d.color || k.color} onChange={v => update({ color: v })}/>
        {d.color && <button onClick={() => update({ color: undefined })} className="text-[11px] text-blue-600 hover:underline -mt-1">ใช้สีตามประเภท</button>}
        <ColorField label="สีข้อความ" value={d.labelColor || '#111827'} onChange={v => update({ labelColor: v })}/>
      </div>
    </div>
  )
}

function ImageSection({ d, asset, update }: { d: DeviceData; asset?: SiteAsset; update: (p: Partial<DeviceData>) => void }) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const upload = async (f: File | undefined) => {
    if (!f) return
    setUploading(true)
    try {
      const fd = new FormData(); fd.append('image', f)
      const r = await api.post('/network-diagrams/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      update({ image: r.data.url, shape: 'plain', w: undefined })
    } catch (e: any) {
      alert(e.response?.data?.message || 'อัปโหลดรูปไม่สำเร็จ')
    }
    setUploading(false)
    if (fileRef.current) fileRef.current.value = ''
  }
  const assetImages = asset?.images || []
  return (
    <div className="rounded-lg border border-gray-200 p-2.5 space-y-2">
      <div className="text-[11px] font-medium text-gray-500 flex items-center gap-1"><ImageIcon size={11}/> รูปภาพแทนไอคอน</div>
      {d.image && (
        <div className="flex items-center gap-2">
          <img src={d.image} alt="" className="w-12 h-12 object-contain rounded border border-gray-200 bg-white"/>
          <div className="flex-1 space-y-1">
            <Seg value={(d.shape || 'tile') === 'plain' ? 'free' : (d.imageFit || 'contain')}
              onChange={v => update(v === 'free' ? { shape: 'plain', w: undefined } : { shape: d.shape === 'plain' ? 'tile' : d.shape, imageFit: v, w: undefined })}
              options={[{ key: 'free', label: 'เต็มรูป', title: 'แสดงรูปเต็มตามสัดส่วนจริง ไม่มีกรอบ' }, { key: 'contain', label: 'ในกรอบ' }, { key: 'cover', label: 'ครอปเต็มกรอบ' }]}/>
            <button onClick={() => update({ image: null })} className="text-[11px] text-red-600 hover:underline">เอารูปออก (ใช้ไอคอน)</button>
          </div>
        </div>
      )}
      {assetImages.length > 0 && (
        <div>
          <div className="text-[10px] text-gray-400 mb-1">รูปจาก Asset {asset?.assetCode}</div>
          <div className="flex flex-wrap gap-1">
            {assetImages.map(u => (
              <button key={u} onClick={() => update({ image: u, ...(d.image ? {} : { shape: 'plain', w: undefined }) })} title="ใช้รูปนี้"
                className={`w-10 h-10 rounded border overflow-hidden bg-white ${d.image === u ? 'ring-2 ring-blue-500 border-blue-500' : 'border-gray-200 hover:border-blue-400'}`}>
                <img src={u} alt="" className="w-full h-full object-cover"/>
              </button>
            ))}
          </div>
        </div>
      )}
      {asset && !assetImages.length && <div className="text-[10px] text-gray-400">Asset นี้ยังไม่มีรูป — เพิ่มรูปได้ที่หน้า Asset</div>}
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={e => upload(e.target.files?.[0])}/>
      <button onClick={() => fileRef.current?.click()} disabled={uploading}
        className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 border border-dashed border-gray-300 rounded-lg text-[11px] text-gray-600 hover:border-blue-400 hover:text-blue-600 disabled:opacity-50">
        {uploading ? <><RefreshCw size={12} className="animate-spin"/> กำลังอัปโหลด...</> : <><Upload size={12}/> อัปโหลดรูปเอง (PNG/JPG, ไม่เกิน 5MB)</>}
      </button>
    </div>
  )
}

function DeviceInfo({ data, asset }: { data: DeviceData; asset?: SiteAsset }) {
  const k = KIND_MAP[data.kind] || KIND_MAP.generic
  const Icon = k.icon
  const rows: [string, string | null | undefined][] = asset
    ? [['Asset Code', asset.assetCode], ['ชื่อ', asset.name], ['ประเภท', asset.type], ['ยี่ห้อ / รุ่น', [asset.brand, asset.model].filter(Boolean).join(' ')], ['Serial', asset.serialNumber], ['IP (Asset)', asset.ipAddress], ['MAC', asset.macEthernet], ['สถานะ', asset.status]]
    : []
  return (
    <div>
      <PanelHead title="รายละเอียดอุปกรณ์"/>
      <div className="p-4 space-y-3 text-xs">
        <div className="flex items-center gap-2">
          {data.image
            ? <img src={data.image} alt="" className="w-12 h-12 object-contain rounded-lg border border-gray-200 bg-white flex-shrink-0"/>
            : <span className="w-9 h-9 rounded-lg flex items-center justify-center text-white flex-shrink-0" style={{ background: data.color || k.color }}><Icon size={18}/></span>}
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 text-sm truncate">{data.label || k.label}</div>
            <div className="text-gray-400">{k.label}</div>
          </div>
        </div>
        {data.sublabel && <div className="text-gray-600">{data.sublabel}</div>}
        {data.ip && <div><span className="text-gray-400">IP: </span><span className="font-mono text-blue-700">{data.ip}</span></div>}
        {data.details && <div className="whitespace-pre-wrap text-gray-600 bg-gray-50 rounded-lg p-2">{data.details}</div>}
        {asset && asset.images?.length > 0 && (
          <div className="flex gap-1 overflow-x-auto">
            {asset.images.map(u => <a key={u} href={u} target="_blank" rel="noreferrer"><img src={u} alt="" className="w-16 h-16 object-cover rounded border border-gray-200 flex-shrink-0"/></a>)}
          </div>
        )}
        {asset && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-2.5 py-1.5 bg-gray-50 text-[11px] font-medium text-gray-500">Asset ที่ผูกไว้</div>
            <table className="w-full">
              <tbody>
                {rows.filter(r => r[1]).map(([l, v]) => (
                  <tr key={l} className="border-t border-gray-100"><td className="px-2.5 py-1 text-gray-400 w-24">{l}</td><td className="px-2.5 py-1 text-gray-800 break-all">{v}</td></tr>
                ))}
              </tbody>
            </table>
            <Link to={`/assets/${asset.id}`} className="flex items-center justify-center gap-1 px-2.5 py-2 border-t border-gray-100 text-blue-600 hover:bg-blue-50">
              <ExternalLink size={12}/> เปิดหน้า Asset
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

function ZonePanel({ data, update, onZ }: { data: ZoneData; update: (p: Partial<ZoneData>) => void; onZ: (d: 1 | -1) => void }) {
  return (
    <div>
      <PanelHead title="Zone / กลุ่ม"><ZButtons onZ={onZ}/></PanelHead>
      <div className="p-4 space-y-3">
        <Field label="ชื่อ Zone"><input value={data.label || ''} onChange={e => update({ label: e.target.value })} className={inputCls}/></Field>
        <Field label="ตำแหน่งชื่อ">
          <select value={data.labelPos || 'bottom-left'} onChange={e => update({ labelPos: e.target.value as ZoneData['labelPos'] })} className={inputCls}>
            <option value="top-left">บนซ้าย</option><option value="top-center">บนกลาง</option>
            <option value="bottom-left">ล่างซ้าย</option><option value="bottom-right">ล่างขวา</option><option value="center">กึ่งกลาง</option>
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="ขนาดตัวอักษร"><input type="number" min={8} max={72} value={data.fontSize || 16} onChange={e => update({ fontSize: +e.target.value })} className={inputCls}/></Field>
          <Field label="รูปทรง">
            <select value={data.shape || 'rect'} onChange={e => update({ shape: e.target.value as ZoneData['shape'] })} className={inputCls}>
              <option value="rect">สี่เหลี่ยม</option><option value="ellipse">วงรี</option>
            </select>
          </Field>
        </div>
        <ColorField label="สีพื้น" value={data.fill ?? '#fde68a'} onChange={v => update({ fill: v })} allowNone/>
        <Field label={`ความทึบ ${Math.round((data.opacity ?? 0.55) * 100)}%`}>
          <input type="range" min={0} max={1} step={0.05} value={data.opacity ?? 0.55} onChange={e => update({ opacity: +e.target.value })} className="w-full"/>
        </Field>
        <ColorField label="สีขอบ" value={data.border || '#78716c'} onChange={v => update({ border: v })}/>
        <div className="grid grid-cols-2 gap-2">
          <Field label="เส้นขอบ">
            <select value={data.borderStyle || 'dashed'} onChange={e => update({ borderStyle: e.target.value as ZoneData['borderStyle'] })} className={inputCls}>
              <option value="solid">เส้นทึบ</option><option value="dashed">เส้นประ</option><option value="dotted">จุด</option><option value="none">ไม่มี</option>
            </select>
          </Field>
          <Field label="ความหนาขอบ"><input type="number" min={0} max={12} step={0.5} value={data.borderWidth ?? 1.5} onChange={e => update({ borderWidth: +e.target.value })} className={inputCls}/></Field>
        </div>
        {data.shape !== 'ellipse' && (
          <Field label={`ความโค้งมุม ${data.radius ?? 16}px`}>
            <input type="range" min={0} max={60} value={data.radius ?? 16} onChange={e => update({ radius: +e.target.value })} className="w-full"/>
          </Field>
        )}
        <ColorField label="สีชื่อ Zone" value={data.labelColor || '#57534e'} onChange={v => update({ labelColor: v })}/>
        <p className="text-[11px] text-gray-400">ลากขอบเพื่อปรับขนาด · ลาก Zone แล้วอุปกรณ์ข้างในจะย้ายตาม</p>
      </div>
    </div>
  )
}

function TextPanel({ data, update, onZ }: { data: TextData; update: (p: Partial<TextData>) => void; onZ: (d: 1 | -1) => void }) {
  return (
    <div>
      <PanelHead title="ข้อความ"><ZButtons onZ={onZ}/></PanelHead>
      <div className="p-4 space-y-3">
        <Field label="ข้อความ"><textarea rows={3} value={data.text || ''} onChange={e => update({ text: e.target.value })} className={inputCls}/></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="ขนาด"><input type="number" min={8} max={96} value={data.fontSize || 14} onChange={e => update({ fontSize: +e.target.value })} className={inputCls}/></Field>
          <Field label="จัดแนว">
            <select value={data.align || 'left'} onChange={e => update({ align: e.target.value as TextData['align'] })} className={inputCls}>
              <option value="left">ซ้าย</option><option value="center">กลาง</option><option value="right">ขวา</option>
            </select>
          </Field>
        </div>
        <div className="flex gap-3 text-xs text-gray-700">
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!data.bold} onChange={e => update({ bold: e.target.checked })}/> ตัวหนา</label>
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!data.italic} onChange={e => update({ italic: e.target.checked })}/> ตัวเอียง</label>
        </div>
        <ColorField label="สีตัวอักษร" value={data.color || '#111827'} onChange={v => update({ color: v })}/>
        <ColorField label="สีพื้นหลัง" value={data.bg || 'transparent'} onChange={v => update({ bg: v === 'transparent' ? undefined : v })} allowNone/>
      </div>
    </div>
  )
}

function nodeName(nodes: DiagramNode[], id: string) {
  const n = nodes.find(x => x.id === id)
  const d: any = n?.data || {}
  return d.label || d.text || d.ip || id
}

function EdgePanel({ edge, update, nodes }: { edge: DiagramEdge; update: (p: Partial<DiagramEdge>, d?: Partial<EdgeData>) => void; nodes: DiagramNode[] }) {
  const d = edge.data || {}
  return (
    <div>
      <PanelHead title="เส้นเชื่อมต่อ"/>
      <div className="p-4 space-y-3">
        <div className="rounded-lg border border-gray-200 p-2.5 space-y-2">
          <div className="text-[11px] font-medium text-gray-500">Port ที่เชื่อมต่อ</div>
          <Field label={`ต้นทาง: ${nodeName(nodes, edge.source)}`}>
            <input value={d.sourcePort || ''} onChange={e => update({}, { sourcePort: e.target.value || undefined })} className={inputCls + ' font-mono'} placeholder="เช่น Gi1/0/1, port 24, WAN1"/>
          </Field>
          <Field label={`ปลายทาง: ${nodeName(nodes, edge.target)}`}>
            <input value={d.targetPort || ''} onChange={e => update({}, { targetPort: e.target.value || undefined })} className={inputCls + ' font-mono'} placeholder="เช่น Gi0/1, eth0, port 1"/>
          </Field>
          <button type="button" onClick={() => update({}, { sourcePort: d.targetPort, targetPort: d.sourcePort })} className="text-[11px] text-blue-600 hover:underline">⇄ สลับ Port ต้นทาง / ปลายทาง</button>
        </div>
        <Field label="ข้อความกลางเส้น (เช่น OC-12, 1Gbps, VLAN 10)">
          <input value={(edge.label as string) || ''} onChange={e => update({ label: e.target.value || undefined })} className={inputCls}/>
        </Field>
        <Field label="ประเภทการเชื่อมต่อ (Preset)">
          <div className="grid grid-cols-1 gap-1">
            {EDGE_PRESETS.map(p => (
              <button key={p.key} onClick={() => update({}, { ...p.data, animated: !!p.data.animated, preset: p.key })}
                className={`flex items-center gap-2 px-2 py-1 rounded-md text-[11px] border ${d.preset === p.key ? 'border-blue-400 bg-blue-50' : 'border-gray-100 hover:bg-gray-50'}`}>
                <svg width="36" height="8"><line x1="0" y1="4" x2="36" y2="4" stroke={p.data.color} strokeWidth={p.data.width} strokeDasharray={DASH[p.data.dash || 'solid']}/></svg>
                {p.label}
              </button>
            ))}
          </div>
        </Field>
        <Field label="รูปแบบเส้นทาง">
          <select value={edge.type || 'default'} onChange={e => update({ type: e.target.value })} className={inputCls}>
            {EDGE_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </Field>
        <ColorField label="สีเส้น" value={d.color || '#1d4ed8'} onChange={v => update({}, { color: v, preset: undefined })}/>
        <div className="grid grid-cols-2 gap-2">
          <Field label="ความหนา"><input type="number" min={0.5} max={16} step={0.5} value={d.width ?? 2} onChange={e => update({}, { width: +e.target.value, preset: undefined })} className={inputCls}/></Field>
          <Field label="ลายเส้น">
            <select value={d.dash || 'solid'} onChange={e => update({}, { dash: e.target.value as EdgeData['dash'], preset: undefined })} className={inputCls}>
              <option value="solid">ทึบ</option><option value="dashed">ประ</option><option value="dotted">จุด</option><option value="long">ประยาว</option>
            </select>
          </Field>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-xs text-gray-700">
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!d.arrowStart} onChange={e => update({}, { arrowStart: e.target.checked })}/> หัวลูกศรต้น</label>
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!d.arrowEnd} onChange={e => update({}, { arrowEnd: e.target.checked })}/> หัวลูกศรปลาย</label>
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!d.animated} onChange={e => update({}, { animated: e.target.checked })}/> เคลื่อนไหว (ทิศทาง traffic)</label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="ขนาดข้อความ"><input type="number" min={8} max={32} value={d.labelSize || 11} onChange={e => update({}, { labelSize: +e.target.value })} className={inputCls}/></Field>
          <Field label="สีข้อความ"><input type="color" value={d.labelColor || '#111827'} onChange={e => update({}, { labelColor: e.target.value })} className="w-full h-7 border border-gray-200 rounded"/></Field>
        </div>
        <Field label="สีพื้นข้อความ"><input type="color" value={d.labelBg || '#ffffff'} onChange={e => update({}, { labelBg: e.target.value })} className="w-full h-7 border border-gray-200 rounded"/></Field>
        {(d.bend || d.sourceShift || d.targetShift) && (
          <button onClick={() => update({}, { bend: undefined, sourceShift: undefined, targetShift: undefined })}
            className="w-full px-2 py-1.5 rounded-lg border border-gray-200 text-[11px] text-gray-600 hover:bg-gray-50">
            คืนรูปเส้นเดิม (ตำแหน่งเส้น / จุดเข้า-ออก)
          </button>
        )}
        {(d.labelOffset || d.sourcePortOffset || d.targetPortOffset) && (
          <button onClick={() => update({}, { labelOffset: undefined, sourcePortOffset: undefined, targetPortOffset: undefined })}
            className="w-full px-2 py-1.5 rounded-lg border border-gray-200 text-[11px] text-gray-600 hover:bg-gray-50">
            คืนตำแหน่งข้อความ / Port ทั้งหมดของเส้นนี้
          </button>
        )}
        <div className="text-[11px] text-gray-400 space-y-0.5">
          <div>• <b className="text-gray-500">จุดกลม</b> กลางเส้น: ลากเพื่อดัดเส้น / ย้ายช่วงกลางของเส้นหักมุม</div>
          <div>• <b className="text-gray-500">จุดสี่เหลี่ยม</b> ที่ปลายเส้น: ลากเพื่อเลื่อนจุดเข้า-ออกตามขอบอุปกรณ์ (กันเส้นทับกัน)</div>
          <div>• ลากปลายสุดของเส้นไปจุดอื่นเพื่อเปลี่ยนจุดเชื่อมต่อ</div>
          <div>• ลากป้าย Port / ข้อความเพื่อย้ายตำแหน่ง · ดับเบิลคลิกเพื่อคืนค่าเดิม</div>
        </div>
      </div>
    </div>
  )
}

function EdgeInfo({ edge, nodes }: { edge: DiagramEdge; nodes: DiagramNode[] }) {
  const p = EDGE_PRESETS.find(x => x.key === edge.data?.preset)
  return (
    <div>
      <PanelHead title="เส้นเชื่อมต่อ"/>
      <div className="p-4 space-y-1.5 text-xs text-gray-700">
        <table className="w-full border border-gray-200">
          <thead><tr className="bg-gray-50 text-[11px] text-gray-500"><th className="px-2 py-1 text-left font-medium">อุปกรณ์</th><th className="px-2 py-1 text-left font-medium">Port</th></tr></thead>
          <tbody>
            <tr className="border-t border-gray-100"><td className="px-2 py-1">{nodeName(nodes, edge.source)}</td><td className="px-2 py-1 font-mono">{edge.data?.sourcePort || '-'}</td></tr>
            <tr className="border-t border-gray-100"><td className="px-2 py-1">{nodeName(nodes, edge.target)}</td><td className="px-2 py-1 font-mono">{edge.data?.targetPort || '-'}</td></tr>
          </tbody>
        </table>
        {edge.label && <div><span className="text-gray-400">ข้อความ: </span>{edge.label as string}</div>}
        {p && <div><span className="text-gray-400">ประเภท: </span>{p.label}</div>}
      </div>
    </div>
  )
}

function MultiPanel({ count, align, distribute, onZ, setKind }: {
  count: number; align: (m: 'l' | 'c' | 'r' | 't' | 'm' | 'b') => void; distribute: (a: 'x' | 'y') => void
  onZ: (d: 1 | -1) => void; setKind: (k: string) => void
}) {
  return (
    <div>
      <PanelHead title={`เลือก ${count} รายการ`}><ZButtons onZ={onZ}/></PanelHead>
      <div className="p-4 space-y-3">
        <Field label="จัดแนว">
          <div className="flex flex-wrap gap-0.5">
            <TbBtn title="ชิดซ้าย" onClick={() => align('l')}><AlignStartVertical size={16}/></TbBtn>
            <TbBtn title="กึ่งกลางแนวนอน" onClick={() => align('c')}><AlignCenterVertical size={16}/></TbBtn>
            <TbBtn title="ชิดขวา" onClick={() => align('r')}><AlignEndVertical size={16}/></TbBtn>
            <TbBtn title="ชิดบน" onClick={() => align('t')}><AlignStartHorizontal size={16}/></TbBtn>
            <TbBtn title="กึ่งกลางแนวตั้ง" onClick={() => align('m')}><AlignCenterHorizontal size={16}/></TbBtn>
            <TbBtn title="ชิดล่าง" onClick={() => align('b')}><AlignEndHorizontal size={16}/></TbBtn>
          </div>
        </Field>
        <Field label="กระจายระยะเท่ากัน (3 รายการขึ้นไป)">
          <div className="flex gap-0.5">
            <TbBtn title="กระจายแนวนอน" onClick={() => distribute('x')} disabled={count < 3}><AlignHorizontalDistributeCenter size={16}/></TbBtn>
            <TbBtn title="กระจายแนวตั้ง" onClick={() => distribute('y')} disabled={count < 3}><AlignVerticalDistributeCenter size={16}/></TbBtn>
          </div>
        </Field>
        <Field label="เปลี่ยนประเภทอุปกรณ์ทั้งหมดที่เลือก">
          <select value="" onChange={e => e.target.value && setKind(e.target.value)} className={inputCls}>
            <option value="">— เลือกประเภท —</option>
            {DEVICE_KINDS.map(x => <option key={x.kind} value={x.kind}>{x.label}</option>)}
          </select>
        </Field>
        <p className="text-[11px] text-gray-400">Shift/Ctrl + คลิก เพื่อเลือกหลายรายการ · Shift + ลาก เพื่อเลือกเป็นกรอบ</p>
      </div>
    </div>
  )
}

function OverviewPanel({ detail, site, editing, settings, setSettings, deviceCount, linkCount, linkedCount, onSyncAssets, onAssetImages }: {
  detail?: DiagramFull; site?: SiteRow; editing: boolean; settings: Settings; setSettings: (p: Partial<Settings>) => void
  deviceCount: number; linkCount: number; linkedCount: number; onSyncAssets: () => void; onAssetImages: (on: boolean) => void
}) {
  return (
    <div>
      <PanelHead title="Diagram"/>
      <div className="p-4 space-y-3 text-xs">
        <div>
          <div className="font-semibold text-gray-900 text-sm">{detail?.name || '-'}</div>
          <div className="text-gray-400 flex items-center gap-1"><MapPin size={11}/> {site?.name}</div>
          {detail?.description && <p className="text-gray-600 mt-1.5 whitespace-pre-wrap">{detail.description}</p>}
          {detail && <p className="text-[11px] text-gray-400 mt-1.5">แก้ไขล่าสุด {new Date(detail.updatedAt).toLocaleString('th-TH')}{detail.updatedBy ? ` โดย ${detail.updatedBy}` : ''}</p>}
        </div>
        <div className="grid grid-cols-3 gap-1.5 text-center">
          <div className="bg-gray-50 rounded-lg py-1.5"><div className="text-base font-semibold text-gray-900">{deviceCount}</div><div className="text-[10px] text-gray-500">อุปกรณ์</div></div>
          <div className="bg-gray-50 rounded-lg py-1.5"><div className="text-base font-semibold text-gray-900">{linkCount}</div><div className="text-[10px] text-gray-500">เส้นเชื่อม</div></div>
          <div className="bg-gray-50 rounded-lg py-1.5"><div className="text-base font-semibold text-gray-900">{linkedCount}</div><div className="text-[10px] text-gray-500">ผูก Asset</div></div>
        </div>

        {editing ? (
          <>
            {linkedCount > 0 && (
              <button onClick={onSyncAssets} className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 text-[11px]">
                <RefreshCw size={12}/> อัปเดตเลข Asset / รุ่น / IP จาก Assets
              </button>
            )}
            {linkedCount > 0 && (
              <div className="flex gap-1.5">
                <button onClick={() => onAssetImages(true)} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 text-[11px]">
                  <ImageIcon size={12}/> ใช้รูปจาก Asset ทั้งหมด
                </button>
                <button onClick={() => onAssetImages(false)} className="px-2 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-[11px]" title="กลับไปใช้ไอคอนสำหรับอุปกรณ์ที่ใช้รูปจาก Asset">
                  ใช้ไอคอน
                </button>
              </div>
            )}
            <div className="pt-2 border-t border-gray-100 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">ตั้งค่า Canvas</div>
            <ColorField label="สีพื้นหลัง" value={settings.bg} onChange={v => setSettings({ bg: v })}/>
            <div className="grid grid-cols-2 gap-2">
              <Field label="กริด">
                <select value={settings.grid} onChange={e => setSettings({ grid: e.target.value as Settings['grid'] })} className={inputCls}>
                  <option value="dots">จุด</option><option value="lines">เส้น</option><option value="cross">กากบาท</option><option value="none">ไม่แสดง</option>
                </select>
              </Field>
              <Field label="ระยะกริด"><input type="number" min={5} max={100} value={settings.gridGap} onChange={e => setSettings({ gridGap: +e.target.value || 20 })} className={inputCls}/></Field>
            </div>
            <div className="flex flex-col gap-1.5 text-gray-700">
              <label className="flex items-center gap-1.5"><input type="checkbox" checked={settings.snap} onChange={e => setSettings({ snap: e.target.checked })}/> Snap to grid</label>
              <label className="flex items-center gap-1.5"><input type="checkbox" checked={settings.minimap} onChange={e => setSettings({ minimap: e.target.checked })}/> แสดง MiniMap</label>
              <label className="flex items-center gap-1.5"><input type="checkbox" checked={settings.stats} onChange={e => setSettings({ stats: e.target.checked })}/> แสดงกล่อง Stats</label>
            </div>
            <div className="pt-2 border-t border-gray-100 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">ค่าเริ่มต้นเส้นเชื่อมใหม่</div>
            <Field label="ประเภทการเชื่อมต่อ">
              <select value={settings.edgePreset} onChange={e => setSettings({ edgePreset: e.target.value })} className={inputCls}>
                {EDGE_PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </Field>
            <Field label="รูปแบบเส้นทาง">
              <select value={settings.edgeType} onChange={e => setSettings({ edgeType: e.target.value })} className={inputCls}>
                {EDGE_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </Field>
            <div className="pt-2 border-t border-gray-100 text-[11px] text-gray-400 space-y-0.5">
              <div className="font-semibold text-gray-500 flex items-center gap-1"><FileJson size={11}/> วิธีใช้</div>
              <div>• ลากจากจุดที่ขอบไอคอนไปยังอุปกรณ์อื่นเพื่อเชื่อมต่อ</div>
              <div>• คลิกอุปกรณ์ / เส้น / Zone เพื่อแก้ไขคุณสมบัติ</div>
              <div>• Ctrl+Z / Ctrl+Y ย้อนกลับ · Ctrl+C / Ctrl+V คัดลอก · Ctrl+D ทำซ้ำ</div>
              <div>• Delete ลบ · Ctrl+A เลือกทั้งหมด · Ctrl+S บันทึก</div>
            </div>
          </>
        ) : (
          <p className="text-[11px] text-gray-400">คลิกอุปกรณ์เพื่อดูรายละเอียดและ Asset ที่ผูกไว้</p>
        )}
      </div>
    </div>
  )
}
